import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationsService } from 'angular2-notifications';
import { finalize, takeUntil } from 'rxjs/operators';
import { TotpSetupResponse } from 'src/app/models/mfa/mfa.model';
import { MfaRecoveryService } from 'src/app/services/general/mfa-recovery.service';
import { MetaService } from 'src/app/services/meta.service';
import { ComponentDestroyClass } from 'src/app/shared/classes/component-destroy.class';
import {
  customPatternValidator,
  customRequiredValidator,
} from 'src/app/shared/utils/form-validators';
import { AccessRulesService } from '../../services/access-rules.service';

/** Recovery link issued by an admin "Reset 2FA": #/mfa-recovery[/<path>]?token=... */
@Component({
  standalone: false,
  selector: 'cms-mfa-recovery',
  templateUrl: './mfa-recovery.component.html',
  styleUrls: ['./mfa-recovery.component.scss'],
  providers: [MfaRecoveryService],
})
export class MfaRecoveryComponent extends ComponentDestroyClass implements OnInit, OnDestroy {
  setupForm: FormGroup;

  confirmForm: FormGroup;

  passwordVisibility = false;

  setupLoading = false;

  confirmLoading = false;

  invalidLink = false;

  setup: TotpSetupResponse = null;

  private path: string;

  private token: string;

  private currentPassword: string;

  private readonly REQUIRED_MESSAGE = 'The field should be filled in';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private metaSvc: MetaService,
    private notificationSvc: NotificationsService,
    private mfaRecoverySvc: MfaRecoveryService,
    private accessRulesService: AccessRulesService,
    private sanitizer: DomSanitizer,
  ) {
    super();
  }

  ngOnInit(): void {
    this.metaSvc.setTitle('Recover Two-Factor Authentication');
    this.path = this.route.snapshot.params.path;
    this.token = this.route.snapshot.queryParams.token;
    this.invalidLink = !this.token;

    this.setupForm = this.fb.group({
      currentPassword: ['', [customRequiredValidator(this.REQUIRED_MESSAGE)]],
    });
    this.confirmForm = this.fb.group({
      code: [
        '',
        [
          customRequiredValidator(this.REQUIRED_MESSAGE),
          customPatternValidator(/^\d{6}$/, 'Code must contain 6 digits'),
        ],
      ],
    });
  }

  ngOnDestroy() {
    super.onDestroy();
  }

  get logo(): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(this.accessRulesService.getLogoBase64());
  }

  get currentPasswordControl() {
    return this.setupForm.get('currentPassword') as FormControl;
  }

  get codeControl() {
    return this.confirmForm.get('code') as FormControl;
  }

  get qrCode() {
    return `data:image/png;base64,${this.setup.qrCodeBase64}`;
  }

  get secretKey() {
    return this.setup.key.match(/.{1,4}/g).join(' ');
  }

  get setupBtnDisabled() {
    return this.setupLoading || this.currentPasswordControl.invalid;
  }

  get confirmBtnDisabled() {
    return this.confirmLoading || this.codeControl.invalid;
  }

  onStartRecovery() {
    if (this.setupBtnDisabled) {
      return;
    }

    this.setupLoading = true;
    this.currentPassword = this.currentPasswordControl.value;

    this.mfaRecoverySvc
      .setup(this.basePayload())
      .pipe(
        finalize(() => {
          this.setupLoading = false;
        }),
        takeUntil(this.componentDestroyed$),
      )
      .subscribe({
        next: (setup: TotpSetupResponse) => {
          this.setup = setup;
        },
        error: (err: any) => {
          this.notificationSvc.error('Error', err.message);
        },
      });
  }

  onConfirmRecovery() {
    if (this.confirmBtnDisabled) {
      return;
    }

    this.confirmLoading = true;

    this.mfaRecoverySvc
      .confirm({ ...this.basePayload(), code: String(this.codeControl.value).trim() })
      .pipe(
        finalize(() => {
          this.confirmLoading = false;
        }),
        takeUntil(this.componentDestroyed$),
      )
      .subscribe({
        next: () => {
          this.notificationSvc.success(
            'Success',
            'Two-Factor Authentication has been set up again. Please sign in.',
          );
          this.router.navigate(['login']);
        },
        error: (err: any) => {
          this.notificationSvc.error('Error', err.message);
        },
      });
  }

  private basePayload() {
    return {
      ...(this.path && { path: this.path }),
      token: this.token,
      currentPassword: this.currentPassword,
    };
  }
}
